import { useCallback, useEffect, useRef } from 'react';
import type { DashboardSectionPanelId } from './dashboardSectionNav';
import {
    sectionPanelForMissingItem,
    type SystemDesignMissingItemId,
} from './systemDesignMissingItems';

export type SystemDesignMissingFixExecutor = (id: SystemDesignMissingItemId) => void;

export type UseSystemDesignMissingFixOrchestratorOptions = {
    dashboardSectionPanel: DashboardSectionPanelId;
    setDashboardSectionPanel: React.Dispatch<React.SetStateAction<DashboardSectionPanelId>>;
};

type PendingFixSection = 'system' | 'customer';

/**
 * Routes "Fill in" / fix-FAB to the owning dashboard section, then runs the matching fix handler.
 */
export function useSystemDesignMissingFixOrchestrator({
    dashboardSectionPanel,
    setDashboardSectionPanel,
}: UseSystemDesignMissingFixOrchestratorOptions) {
    const systemExecutorRef = useRef<SystemDesignMissingFixExecutor | null>(null);
    const customerExecutorRef = useRef<SystemDesignMissingFixExecutor | null>(null);
    const pendingFixRef = useRef<SystemDesignMissingItemId | null>(null);
    const pendingSectionRef = useRef<PendingFixSection | null>(null);

    const bindExecutor = useCallback((executor: SystemDesignMissingFixExecutor | null) => {
        systemExecutorRef.current = executor;
    }, []);

    const bindCustomerExecutor = useCallback((executor: SystemDesignMissingFixExecutor | null) => {
        customerExecutorRef.current = executor;
    }, []);

    const runSystemExecutor = useCallback((id: SystemDesignMissingItemId) => {
        requestAnimationFrame(() => systemExecutorRef.current?.(id));
    }, []);

    const runCustomerExecutor = useCallback((id: SystemDesignMissingItemId) => {
        requestAnimationFrame(() => customerExecutorRef.current?.(id));
    }, []);

    const fixMissingItem = useCallback(
        (id: SystemDesignMissingItemId) => {
            const targetSection = sectionPanelForMissingItem(id);

            if (targetSection === 'utility') {
                if (dashboardSectionPanel === 'utility') return;
                if (dashboardSectionPanel === 'all') {
                    runSystemExecutor(id);
                    return;
                }
                setDashboardSectionPanel('utility');
                return;
            }

            if (targetSection === 'customer') {
                if (dashboardSectionPanel === 'customer' || dashboardSectionPanel === 'all') {
                    runCustomerExecutor(id);
                    return;
                }
                pendingFixRef.current = id;
                pendingSectionRef.current = 'customer';
                setDashboardSectionPanel('customer');
                return;
            }

            if (dashboardSectionPanel === 'system' || dashboardSectionPanel === 'all') {
                runSystemExecutor(id);
                return;
            }

            pendingFixRef.current = id;
            pendingSectionRef.current = 'system';
            setDashboardSectionPanel('system');
        },
        [dashboardSectionPanel, runCustomerExecutor, runSystemExecutor, setDashboardSectionPanel],
    );

    useEffect(() => {
        const pending = pendingFixRef.current;
        const pendingSection = pendingSectionRef.current;
        if (!pending || !pendingSection) return;

        if (pendingSection === 'customer') {
            if (dashboardSectionPanel !== 'customer' && dashboardSectionPanel !== 'all') return;
        } else if (dashboardSectionPanel !== 'system' && dashboardSectionPanel !== 'all') {
            return;
        }

        pendingFixRef.current = null;
        pendingSectionRef.current = null;

        if (pendingSection === 'customer') {
            runCustomerExecutor(pending);
        } else {
            runSystemExecutor(pending);
        }
    }, [dashboardSectionPanel, runCustomerExecutor, runSystemExecutor]);

    return { fixMissingItem, bindExecutor, bindCustomerExecutor };
}
